import { pushAnalyticsEvent } from '../hooks/analytics/useAnalytics';
import { getAnalyticsEnabled } from './analyticsState';
import {
  CORRESPONDENT_USER_ROLE,
  MEMBER_USER_ROLE,
  STAFF_USER_ROLE,
} from '../environment';
import { UserDetails, UserRoleIdent } from '../models/analytics';
import { ProVesselTypes } from '../models/pro.model';
import { Token } from '../user/user.slice';

/**
 * Returns the anon identifier (first part) of the user's sub, or undefined if not present.
 */
export const getUserAnonymisedId = (
  token: Token | null
): string | undefined => {
  const sub = token?.sub;
  return sub ? sub.split('-')[0] : undefined;
};

/**
 * Returns the user's role as a UserRoleIdent enum value based on a token object.
 * Role precedence is: STAFF > MEMBER > CORRESPONDENT > GUEST.
 * The function checks for each role in order and returns the highest found.
 *
 * @param {Token | null} token - The user token object.
 * @returns {UserRoleIdent} The user's role: STAFF, MEMBER, CORRESPONDENT, or GUEST.
 */
export function getUserRoleIdentifier(token: Token | null): UserRoleIdent {
  const roles = token?.roles;
  if (!roles || roles.length === 0) {
    return UserRoleIdent.GUEST;
  }
  if (roles.includes(STAFF_USER_ROLE)) {
    return UserRoleIdent.STAFF;
  }
  if (roles.includes(MEMBER_USER_ROLE)) {
    return UserRoleIdent.MEMBER;
  }
  if (roles.includes(CORRESPONDENT_USER_ROLE)) {
    return UserRoleIdent.CORRESPONDENT;
  }
  return UserRoleIdent.GUEST;
}

/**
 * Formats user details into a single analytics label string for event logging.
 *
 * Example output: 'User:abc123 | CompanyName:Acme | JobTitle:Captain '
 *
 * @param {UserDetails} user - The user details object.
 * @returns {string | null} The formatted label string, or null if no user provided.
 */
export function getUserAnalyticsEventLabel(user?: UserDetails): string | null {
  if (!user) return null;
  const userParts = [];
  if (user.userAnonymisedId) userParts.push(`User:${user.userAnonymisedId}`);
  if (user.companyName) userParts.push(`CompanyName:${user.companyName}`);
  if (user.jobTitle) userParts.push(`JobTitle:${user.jobTitle}`);
  const userLabel = userParts.join(' | ');
  return userLabel;
}

/**
 * Formats user details into a single analytics label string for event logging.
 *
 * Example output:  'Role:STAFF'
 *
 * @param {UserDetails} user - The user details object.
 * @returns {string | null} The formatted label string, or null if no user provided.
 */
export function getRoleAnalyticsEventLabel(user?: UserDetails): string | null {
  if (!user) return null;
  return user.role ? `Role:${user.role}` : null;
}

/**
 * Returns an array of analytics label fragments for a user, for use in event logging.
 *
 * Example output: ['User:abc123', 'CompanyName:Acme', 'JobTitle:Captain', 'Role:STAFF']
 *
 * @param {UserDetails} user - The user details object.
 * @returns {string[]} Array of label fragments (empty if no user provided).
 */
export function getUserLoginAnalyticsLabels(user: UserDetails): string[] {
  // TODO: Optional refactor to return UNKNOWN for missing fields
  if (!user) return [];
  const labels: string[] = [];
  if (user.userAnonymisedId) labels.push(`User:${user.userAnonymisedId}`);
  if (user.companyName) labels.push(`CompanyName:${user.companyName}`);
  if (user.jobTitle) labels.push(`JobTitle:${user.jobTitle}`);
  if (user.role) labels.push(`Role:${user.role}`);
  return labels;
}

/**
 * Formats a count value into an analytics label string for event logging.
 *
 * Example output: 'Count:3'
 *
 * @param {number} count - The count value to include in the label.
 * @returns {string | null} The formatted label string, or null if count is falsy.
 */
export function getCountAnalyticsEventLabel(count: number): string | null {
  if (!count) return null;
  return `Count:${count}`;
}

/**
 * Returns an array of analytics label strings for PRO report details (unlocode and vessel type).
 *
 * Example output: ['UNLOCODE:USNYC', 'VesselType:Bulk']
 *
 * @param {string} unlocode - The port unlocode (may contain spaces).
 * @param {string} vesselType - The vessel type.
 * @returns {string[]} Array of formatted label strings, or empty array if both values are missing.
 */
export function getProReportAnalyticsEventLabels(
  unlocode: string,
  vesselType: string
): string[] {
  if (!unlocode && !vesselType) return [];
  const labels: string[] = [];
  const formattedUnlocode = unlocode ? unlocode.replace(/\s+/g, '') : '';
  if (formattedUnlocode) labels.push(`UNLOCODE:${formattedUnlocode}`);
  if (vesselType) labels.push(`VesselType:${vesselType}`);
  return labels;
}

/**
 * Logs a single analytics event for a feature and action, combining all label details into one string.
 *
 * Event format: Event::<feature>::<action>::<label>
 *
 * - All label strings are joined with ' | ' and appended as a single label.
 * - This structure is optimized for Fathom Analytics, which does not support grouping or breakdowns in the dashboard.
 * - To analyze by label (e.g., by role, vessel type), filter events by searching for label fragments in Fathom.
 *
 * @param {string} feature - The context or section of the app where the event occurs.
 * @param {string} action - The specific action or event.
 * @param {string[]} labels - Array of label strings to combine and append to the event.
 * @returns {void}
 */

export function logAnalyticsEvent(
  feature: string,
  action: string,
  labels?: (string | null)[]
) {
  if (!feature || !action) {
    return;
  }
  if (!getAnalyticsEnabled()) {
    return;
  }
  const filteredLabels = labels?.filter(Boolean);
  if (filteredLabels && filteredLabels.length > 0) {
    filteredLabels.forEach((label) => {
      pushAnalyticsEvent(`Event::${feature}::${action}::${label}`);
    });
  } else {
    pushAnalyticsEvent(`Event::${feature}::${action}`);
  }
}

/**
 * Returns the vessel type string for analytics logging.
 * Converts NO_VESSEL_TYPE to 'NonVesselSpecific', otherwise returns the vessel type value as-is.
 *
 * @param {ProVesselTypes} vesselType - The vessel type value from the enum.
 * @returns {string} The analytics vessel type string ('NonVesselSpecific' or the vessel type).
 */
export function getAnalyticsVesselType(vesselType: ProVesselTypes): string {
  return vesselType === ProVesselTypes.NO_VESSEL_TYPE
    ? 'NonVesselSpecific'
    : vesselType;
}
